<template>
  <v-data-table
    :headers="eventHeaders"
    :items="enrichedEvents"
    :item-key="mergedView ? 'index' : 'id'"
    :sort-by="'start'"
    :search="search"
    :custom-filter="customFilter"
    group-by="date"
    :show-group-by="true"
    :items-per-page="mergedView ? -1 : 10"
  >
    <template v-slot:top>
      <v-text-field
        v-if="adminView"
        v-model="search"
        label="Search"
        append-icon="mdi-magnify"
      />
    </template>
    <template v-slot:[`group.header`]="{ items }">
      {{ formatIsoDate(items[0].date) }}
    </template>
    <template v-slot:[`item.time`]="{ item }">
      <template v-if="item.start && item.end && !item.dateTimeTbd">
        {{ militaryToAmPm(item.start) }} - {{ militaryToAmPm(item.end) }}
      </template>
      <template v-else>
        TBD
      </template>
      <span v-if="mergedView && item.author" class="event-author">{{ item.author.fullName }}</span>
    </template>
    <template v-slot:[`item.rsvpData.requiresRsvp`]="{ item }">
      <div v-if="item.rsvpData.requiresRsvp" class="yes-no">
        <!-- If user is an admin, they can still modify these values. -->
        <v-checkbox
          :disabled="(!adminView && item.rsvpData.rsvpConfirmed) || ambassadorView || viewerView"
          v-model="item.rsvpData.rsvpYes"
          label="Yes"
          hide-details
          @change="confirmRsvp(item, null, 'yes')"
        ></v-checkbox>
        <v-checkbox
          :disabled="(!adminView && item.rsvpData.rsvpConfirmed) || ambassadorView || viewerView"
          v-model="item.rsvpData.rsvpNo"
          label="No"
          hide-details
          @change="confirmRsvp(item, null, 'no')"
        ></v-checkbox>
      </div>
    </template>
    <template v-slot:[`item.rsvpData.requiresPlusOneRsvp`]="{ item }">
      <div v-if="item.rsvpData.requiresPlusOneRsvp" class="yes-no">
        <v-checkbox
          :disabled="(!adminView && item.rsvpData.plusOneRsvpConfirmed) || ambassadorView || viewerView"
          v-model="item.rsvpData.plusOneRsvpYes"
          label="Yes"
          hide-details
          @change="confirmRsvp(item, 'plus-one', 'yes')"
        ></v-checkbox>
        <v-checkbox
          :disabled="(!adminView && item.rsvpData.plusOneRsvpConfirmed) || ambassadorView || viewerView"
          v-model="item.rsvpData.plusOneRsvpNo"
          label="No"
          hide-details
          @change="confirmRsvp(item, 'plus-one', 'no')"
        ></v-checkbox>
      </div>
    </template>

    <template v-slot:[`item.date`]="{ item }">
      {{ formatIsoDate(item.date) }}
    </template>

    <template v-slot:[`item.description`]="{ item }">
      <span v-if="adminView && item.hideOnGlobalSchedule">
        <v-icon color="primary" class="hidden-event-marker">mdi-domino-mask</v-icon>
      </span>
      <span class="formatted">{{ item.description }}</span>
      <span v-if="item.sessionDescription" class="formatted session-description">{{ item.sessionDescription }}</span>
      <span v-if="adminView && rsvpTotals && rsvpTotals[item.id]">
        <br>
        <v-btn
          small
          color="primary"
          class="pa-1 mb-2 rsvp-total-btn"
          @click="openRsvpModal(item)"
        >
          Total RSVPed: {{ rsvpTotals[item.id] }}
        </v-btn>
      </span>
    </template>

    <template v-slot:[`item.location`]="{item}">
      <span v-if="item.location" class="location-text">{{ locationById(item.location) }}</span>
    </template>

    <template v-slot:[`item.tags`]="{item}">
      <v-chip v-for="tag in item.tags" :key="tag.id" small>
        {{ tag.length >= 20 && authorById(tag) ? authorById(tag).fullName : tag.toUpperCase() }}
      </v-chip>
    </template>

    <template v-slot:[`item.rsvpTags`]="{item}">
      <v-chip v-for="tag in item.rsvpTags" :key="tag.id" small>
        {{ tag.length >= 20 && authorById(tag) ? authorById(tag).fullName : tag.toUpperCase() }}
      </v-chip>
    </template>

    <template v-slot:[`item.categoryId`]="{item}">
      <v-chip
        v-for="categoryId in getValidCategoryIds(item.categoryIds)"
        :key="categoryId"
        outlined
        color="primary"
        small
        class="mr-1 mb-1"
      >
        {{ eventCategoryById(categoryId).name }}
      </v-chip>
    </template>

    <template v-slot:[`item.actions`]="{ item }">
      <v-icon small @click="editEvent(item)" class="pa-1">
        mdi-pencil
      </v-icon>
      <v-icon
        small
        @click="deleteEvent(item)"
        color="error"
        class="pa-1"
      >
        mdi-delete
      </v-icon>
    </template>
  </v-data-table>
</template>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex'
import { httpsCallable } from 'firebase/functions'

export default {
  name: 'ScheduleSingleTable',
  components: {
  },
  props: {
    events: {
      type: Array,
      default: () => []
    },
    eventHeaders: {
      type: Array,
      default: () => []
    },
    mergedView: {
      type: Boolean,
      default: false
    },
    targetAuthorData: {
      type: Object,
      default: () => {}
    },
    rsvpTotals: {
      type: Object,
      default: () => {}
    },
    rsvpAuthorsByEventId: {
      type: Object,
      default: () => {}
    }
  },
  data: () => ({
    search: ''
  }),

  computed: {
    ...mapState([
      'fbFunctions'
    ]),
    ...mapGetters([
      'locationById',
      'authorById',
      'userRole',
      'eventCategoryById'
    ]),
    adminView () {
      return this.userRole === 'admin' || this.userRole === 'viewer'
    },
    ambassadorView () {
      return !!this.ambassadorId
    },
    viewerView () {
      return this.userRole === 'viewer'
    },
    enrichedEvents () {
      if (!this.events) return []
      return this.events.map(event => {
        const eventCopy = { ...event }
        if (event.categoryIds && event.categoryIds.length > 0) {
          eventCopy.categoryNames = event.categoryIds
            .map(id => this.eventCategoryById(id))
            .filter(cat => cat !== null)
            .map(cat => cat.name)
        }
        if (event.location && this.locationById(event.location)) {
          eventCopy.locationName = this.locationById(event.location)
        }
        return eventCopy
      })
    }
  },

  methods: {
    ...mapActions([
      'firestoreDeleteEvent'
    ]),
    ...mapMutations([
      'setGlobalNotification'
    ]),
    getValidCategoryIds (categoryIds) {
      if (!categoryIds || !Array.isArray(categoryIds)) return []
      return categoryIds.filter(id => this.eventCategoryById(id) !== null)
    },
    customFilter (value, search, item) {
      if (!search) return true
      const searchLower = search.toLowerCase()

      // Search in all string properties
      for (const key in item) {
        if (item[key] != null) {
          const value = String(item[key]).toLowerCase()
          if (value.includes(searchLower)) {
            return true
          }
        }
      }

      // Also search in category names if they exist
      if (item.categoryIds && item.categoryIds.length > 0) {
        for (const categoryId of item.categoryIds) {
          const category = this.eventCategoryById(categoryId)
          if (category && category.name.toLowerCase().includes(searchLower)) {
            return true
          }
        }
      }

      // Also search in location name if it exists
      if (item.location && this.locationById(item.location)) {
        const locationName = this.locationById(item.location).toLowerCase()
        if (locationName.includes(searchLower)) {
          return true
        }
      }

      return false
    },
    editEvent (event) {
      this.$root.$emit('showEventEditor', event)
    },
    openRsvpModal (event) {
      const authors = this.rsvpAuthorsByEventId && this.rsvpAuthorsByEventId[event.id]
        ? this.rsvpAuthorsByEventId[event.id]
        : []
      this.$root.$emit('showRsvpListModal', { event, authors })
    },
    deleteEvent (event) {
      if (confirm('Are you sure you want to delete this event?')) {
        this.firestoreDeleteEvent(event.id)
      }
    },
    async confirmRsvp (event, type, inputStatus) {
      console.log('Confirm RSVP for', event)
      let status = inputStatus
      if (type === 'plus-one') {
        // Clear the other input if it's the opposite of the one being set.
        if (inputStatus === 'yes') {
          event.rsvpData.plusOneRsvpNo = false
        } else if (inputStatus === 'no') {
          event.rsvpData.plusOneRsvpYes = false
        }
        // If neither are set, set status to null.
        if (!event.rsvpData.plusOneRsvpYes && !event.rsvpData.plusOneRsvpNo) {
          status = null
        }
      } else {
        if (inputStatus === 'yes') {
          event.rsvpData.rsvpNo = false
        } else if (inputStatus === 'no') {
          event.rsvpData.rsvpYes = false
        }
        if (!event.rsvpData.rsvpYes && !event.rsvpData.rsvpNo) {
          status = null
        }
      }
      try {
        const res = await httpsCallable(this.fbFunctions, 'rsvpToEvent')({
          eventId: event.id,
          authorId: this.targetAuthorData.id,
          status,
          type
        })
        console.log(res)
        this.setGlobalNotification({
          val: `Thank you for confirming your RSVP for ${event.description}. Your response has been recorded.`,
          timeout: -1,
          type: 'success'
        })
      } catch (err) {
        console.log(err)
        this.setGlobalNotification({
          val: `Error confirming your RSVP for ${event.description}.`,
          type: 'error'
        })
      }
    }
  },

  async created () {
    this.ambassadorId = this.$route.query.ambassador
  }
}
</script>

<style lang="scss" scoped>
.event-author {
  display: block;
  font-weight: 700;
  line-height: 100%;
  color: gray;
  margin-bottom: 5px;
}

.location-text {
  white-space: pre-wrap;
}

.session-description {
  display: block;
  margin-top: 0.5em;
  padding-top: 0.2em;
  border-top: solid 1px rgba(0, 0, 0, 0.12);
}

.yes-no {
  display: flex;
  gap: 12px;

  .v-input {
    margin-top: 0;
    padding-top: 0;
  }
  ::v-deep .v-input--selection-controls__input {
    margin-right: 4px;
  }

  @media print {
    display: none;
  }
}
</style>
